import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { healthKey } from '@/api/lockers';
import type { HealthResponse, LockerDetail, LockerHealth } from '@/api/types';
import { useToken } from '@/auth/session';

const RECONNECT_MS = 3000;

function matches(l: LockerHealth, id: unknown) {
    return l.id === id || String(l.id_db) === String(id);
}

/**
 * Живые обновления от device-gateway (/ws/admin): статус связи и платы постаматов.
 * Патчим кэш /api/health — экраны просто читают useLockersHealth и перерисовываются.
 */
export function useAdminSocket() {
    const token = useToken();
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!token) return;
        let ws: WebSocket | null = null;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let stopped = false;

        const connect = () => {
            const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
            ws = new WebSocket(`${proto}://${window.location.host}/ws/admin?token=${encodeURIComponent(token)}`);

            ws.onopen = () => {
                queryClient.invalidateQueries({ queryKey: healthKey });
            };

            ws.onmessage = (event) => {
                let data: any;
                try {
                    data = JSON.parse(event.data);
                } catch {
                    return;
                }
                if (data?.type === 'cell_update') {
                    // Дверца открылась/закрылась — патчим открытую карточку постамата.
                    queryClient.setQueriesData<LockerDetail>({ queryKey: ['locker'] }, (prev) => {
                        if (!prev?.cells) return prev;
                        if (prev.locker_id !== data.locker_id && String(prev.id) !== String(data.locker_id)) return prev;
                        return {
                            ...prev,
                            cells: prev.cells.map((c) =>
                                String(c.cell_number) === String(data.cell) ? { ...c, is_open: !!data.is_open } : c
                            ),
                        };
                    });
                    return;
                }
                if (data?.type !== 'locker_status') return;
                queryClient.setQueryData<HealthResponse>(healthKey, (prev) => {
                    if (!prev?.lockers) return prev;
                    return {
                        ...prev,
                        lockers: prev.lockers.map((l) => {
                            if (!matches(l, data.locker_id)) return l;
                            const next: LockerHealth = { ...l };
                            if (data.ws_connected !== undefined) next.ws_connected = !!data.ws_connected;
                            if (data.board_connected !== undefined) next.board_connected = !!data.board_connected;
                            for (const key of [
                                'display_name',
                                'location_name',
                                'software_type',
                                'version_name',
                                'version_code',
                                'total_cells',
                                'last_online_at',
                                'last_ws_offline_at',
                            ] as const) {
                                if (data[key]) (next as any)[key] = data[key];
                            }
                            return next;
                        }),
                    };
                });
            };

            ws.onclose = (event) => {
                ws = null;
                // 4001/4003 — токен отклонён: переподключаться бессмысленно, 401 от REST разлогинит.
                if (stopped || event.code === 4001 || event.code === 4003) return;
                timer = setTimeout(connect, RECONNECT_MS);
            };
        };

        connect();
        return () => {
            stopped = true;
            if (timer) clearTimeout(timer);
            ws?.close();
        };
    }, [token, queryClient]);
}
